import { z } from 'zod';
/**
 * calendar#37 - Data & settings > Payors (Chase 10-08: the source of payors, between Locations and
 * Holidays). One list: RCM's Chargemaster > Payors (rcm_payors; decision 24a, Pranav 10-09). The
 * EHR reads it under RCM's column grants (rcm-credify migrations 005 + 012) and saves through RCM's
 * PUT /api/payors/:id with the user's own token. RCM's Payors page, the Calendar (which copies
 * RCM's payors on every load) and the Demographics payor dropdown all see the same rows.
 */
/** RCM's PAYER_CLASS_LABEL, same keys and labels. */
export const PAYOR_CLASSES = [
    { key: 'commercial', label: 'Commercial' },
    { key: 'medicare', label: 'Medicare' },
    { key: 'medicaid', label: 'Medi-Cal' },
    { key: 'selfpay', label: 'Self-Pay' },
];
export const payorClassLabel = (key) => PAYOR_CLASSES.find((c) => c.key === key)?.label ?? key;
/** RCM's US_STATES (the 50 states and DC), same order. */
export const PAYOR_STATES = [
    'AK', 'AL', 'AR', 'AZ', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL', 'GA', 'HI', 'IA', 'ID', 'IL', 'IN',
    'KS', 'KY', 'LA', 'MA', 'MD', 'ME', 'MI', 'MN', 'MO', 'MS', 'MT', 'NC', 'ND', 'NE', 'NH', 'NJ',
    'NM', 'NV', 'NY', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VA', 'VT', 'WA',
    'WI', 'WV', 'WY',
]; // prettier-ignore
/**
 * ehr#428: the RCM payor id picked on Demographics -> Insurance / Payor (`insurance.payerRcmId`),
 * or null. putDemographics copies it to contacts."payerRcmId" so RCM can read it; the backfill
 * script uses the same rule.
 */
export function payerRcmIdOf(demographics) {
    const ins = demographics?.insurance;
    const v = ins?.payerRcmId;
    const id = typeof v === 'string' ? v.trim() : '';
    return id && id.length <= 64 ? id : null;
}
export const PAYOR_ZIP_RE = /^\d{5}(-\d{4})?$/;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
export const blankRcmPayor = () => ({
    id: null,
    name: '',
    class: 'commercial',
    payerId: '',
    street: '',
    city: '',
    addrState: '',
    zip: '',
    contractStart: '',
    contractEnd: '',
    active: true,
});
const sameName = (a, b) => a.trim().toLowerCase() === b.trim().toLowerCase();
/**
 * {} when valid, else { field: message }. RCM's own rule for duplicates: one name per state
 * (isDuplicatePayor), so "Aetna" can be in CA and in TX. `existing` is the list; the record's
 * own id is skipped.
 */
export function validateRcmPayor(rec, existing) {
    const e = {};
    const name = rec.name.trim();
    const st = rec.addrState.toUpperCase();
    if (!name)
        e.name = 'Enter the payor name.';
    else if (existing.some((p) => p.id !== rec.id && sameName(p.name, name) && p.addrState.toUpperCase() === st))
        e.name = st
            ? `A payor named "${name}" is already in ${st}.`
            : `A payor named "${name}" with no state is already in the list.`;
    if (!PAYOR_CLASSES.some((c) => c.key === rec.class))
        e.class = 'Pick a class.';
    if (rec.zip.trim() && !PAYOR_ZIP_RE.test(rec.zip.trim()))
        e.zip = 'Use 5 digits, or 5 + 4 (95814-1234).';
    if (rec.contractStart && rec.contractEnd && rec.contractEnd < rec.contractStart)
        e.contractEnd = 'The contract ends before it starts.';
    return e;
}
/** A-Z by name, then state; `status` and `cls` ('' or 'all' = any) narrow it; `query` matches
 *  the name, class, payer ID, city and state, the way RCM's Payors search does. */
export function filterRcmPayors(list, query, status, cls) {
    const q = query.trim().toLowerCase();
    return [...list]
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) ||
        a.addrState.localeCompare(b.addrState))
        .filter((p) => {
        if (status === 'active' && !p.active)
            return false;
        if (status === 'inactive' && p.active)
            return false;
        if (cls && cls !== 'all' && p.class !== cls)
            return false;
        if (!q)
            return true;
        return `${p.name} ${payorClassLabel(p.class)} ${p.payerId} ${p.city} ${p.addrState}`
            .toLowerCase()
            .includes(q);
    });
}
/** The contract end date's state: expired, ending within 90 days, or neither. `today` is yyyy-mm-dd. */
export function contractState(contractEnd, today) {
    if (!ISO_DATE_RE.test(contractEnd))
        return null;
    if (contractEnd < today)
        return 'expired';
    const soon = new Date(`${today}T00:00:00Z`);
    soon.setUTCDate(soon.getUTCDate() + 90);
    return contractEnd <= soon.toISOString().slice(0, 10) ? 'expiring' : null;
}
const CLASS_KEYS = PAYOR_CLASSES.map((c) => c.key);
const isoOrEmpty = z
    .string()
    .trim()
    .refine((v) => v === '' || ISO_DATE_RE.test(v), { message: 'Use a yyyy-mm-dd date.' })
    .default('');
/** The EHR API's create / update body. RCM re-checks the id; the EHR checks duplicates first. */
export const rcmPayorInputSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    class: z.enum(CLASS_KEYS),
    payerId: z.string().trim().max(40).default(''),
    street: z.string().trim().max(200).default(''),
    city: z.string().trim().max(100).default(''),
    addrState: z.union([z.literal(''), z.enum(PAYOR_STATES)]).default(''),
    zip: z
        .string()
        .trim()
        .refine((v) => v === '' || PAYOR_ZIP_RE.test(v), {
        message: 'Use 5 digits, or 5 + 4 (95814-1234).',
    })
        .default(''),
    contractStart: isoOrEmpty,
    contractEnd: isoOrEmpty,
    active: z.boolean(),
})
    .refine((p) => !p.contractStart || !p.contractEnd || p.contractEnd >= p.contractStart, {
    message: 'The contract ends before it starts.',
    path: ['contractEnd'],
});
//# sourceMappingURL=payors.js.map